/**
 * @class constructs an element held in an object.
 * v.1.2
 */
class Element
{
    /**
     * @param {object} - object of element properties:
     * - classes: array of classes or string of single class/multiple classes seperated by spaces.
     * - content: string or node to go in the element
     * - id: string for ID
     * - element: string for type of element e.g 'div', 'ul', 'li'.
     * - attributions: object (or array of objects) containing props 'attribute' and 'value' (optional).
     */
    constructor({classes = null, content = null, id = null, element = 'div', attributions = null} = {})
    {
        this.element = document.createElement(element);
        if (classes)
        {
            this.addClass(classes);
        }
        if (content)
        {
            this.updateContent(content)
        }
        if (id)
        {
            this.addID(id)
        }
        if (attributions)
        {
            Array.isArray(attributions) ?
                this.addMultipleAttributes(attributions)
                :
                typeof attributions === 'object'
                    ?
                    this.addAttribute(attributions.attribute, attributions.value)
                    :
                    this.throwError('Element constructor: attributions must be an object or an array of objects. {attribute, value}');
        }
        this.element.hidden = false;

        return this;
    }

    /**
     * animate the element using animate.css classes
     * @param {string} effect - the class of the effect to use. REQUIRED
     * @param {int} speed - speed of the effect. OPTIONAL - defaults to previously set or 1000ms
     * @param {boolean} infinite - repeat the effect infinitely or not. OPTIONAL - defaults to false.
     * @param {boolean} hideElementAfter - hide the element after animation complete. OPTIONAL - defaults to false.
     */
    animateTheElement(effect, speed, infinite, hideElementAfter)
    {
        if (!this.hasClass('animated'))
        {
            this.addClass('animated')
        }

        if (speed)
        {
            this.element.classList.forEach((className) =>
            {
                if (className.startsWith('speed-'))
                {
                    this.element.classList.remove(className);
                }
            });

            speed = (Math.round(speed / 100) * 100);
            const speedClass = 'speed-' + speed;
            this.addClass(speedClass);
        }

        this.addClass(effect);

        if (infinite)
        {
            this.addClass('infinite');
        }
        else
        {
            this.removeClass('infinite');
            setTimeout(function (element)
            {
                if (hideElementAfter)
                {
                    element.hideTheElement();
                }
                element.removeClass(effect);
            }, speed + 100, this);
        }

        return this;
    }

    /**
     * animate the element in or out of view using animate.css classes
     * @param {string} effect - the class of the effect to use. REQUIRED
     * @param {int} speed - speed of the effect. OPTIONAL - defaults to previously set or 1000ms
     */
    animateTheElementInOrOut(effect, speed, forceClose)
    {
        if (!this.element.hidden || forceClose)
        {
            this.animateTheElement(effect, speed, false, true);
        }
        else
        {
            this.showTheElement(true);
            this.animateTheElement(effect, speed, false, false);
        }

        return this;
    }

    addAndRemoveClass(add, remove)
    {
        this.addClass(add);
        this.removeClass(remove, false);

        return this;
    }

    /**
     * Add class(es) to the element
     * @param {string / array} classes - an array of classes to add to the element
     * - also accepts a single class or a list of classes as a string
     * (each class to be seperated by a space)
     */
    addClass(classes)
    {
        if (typeof classes === 'string')
        {
            classes = classes.split(' ');
        }

        for (const c of classes)
        {
            if (c !== '')
            {
                this.element.classList.add(c);
            }
        }

        return this;
    }

    /**
     * add styleS to the element
     * @param {array} arrayOfStyles - array of objects with properties 'property' and 'style'
     */
    addMultipleStyles(arrayOfStyles)
    {
        for (const style in arrayOfStyles)
        {
            this.addStyle(style.property, style.style);
        }

        return this;
    }

    /**
     * add style to the element
     * @param {string} property - style property
     * @param {string} value - style value
     */
    addStyle(property, value)
    {
        this.element.style[property] = value;

        return this;
    }

    /**
     *  set element style="display: block" (or display type defined in the parameter)
     * @param {string} display - display declaration (accepts any display css declarations: inline, inline-block etc)
     * @param {boolean} removeStyleAttribute - show the element by removing style attribute rather than changing the display declaration within the style attribute
     */
    showTheElement(removeStyleAttribute, display = 'block')
    {
        if (removeStyleAttribute)
        {
            this.element.removeAttribute('style');
        }
        else
        {
            this.element.style.display = display;
        }
        this.element.hidden = false;

        return this;
    }

    /**
     * set element style="display: none"
     */
    hideTheElement()
    {
        this.element.style.display = 'none';
        this.element.hidden = true;

        return this;
    }

    toggleTheElement(removeStyleAttributeOnShow, forceClose, display = 'block')
    {
        if (forceClose)
        {
            this.hideTheElement();
        }
        else
        {
            this.element.hidden ? this.showTheElement(removeStyleAttributeOnShow, display) : this.hideTheElement();
        }

        return this;
    }

    /**
     * Remove class(es) from the element
     * @param {string / array} classes - an array of classes to remove from the element
     * - also accepts a single class or a list of classes as a string
     * (each class to be separated by a space)
     * @param {boolean} invert - invert the classes, to remove all but those classes.
     */
    removeClass(classes, invert)
    {
        if (!classes || invert)
        {
            this.element.className = '';
        }

        if (classes)
        {
            if (typeof classes === 'string')
            {
                classes = classes.split(' ');
            }

            for (const c of classes)
            {
                if (c !== '')
                {
                    invert ? this.element.classList.add(c) : this.element.classList.remove(c);
                }
            }
        }

        return this;
    }

    /**
     * Remove class(es) to the element if they exist, add them if they do not.
     * @param {array / string} classes - an array of classes to add/remove. REQUIRED.
     * - also accepts a single class or a list of classes as a string (each class to be seperated by a space)
     */
    toggleClass(classes)
    {
        if (typeof classes === 'string')
        {
            classes = classes.split(' ');
        }
        for (const c of classes)
        {
            if (c !== '')
            {
                this.hasClass(c)
                    ?
                    this.element.classList.remove(c)
                    :
                    this.element.classList.add(c);
            }
        }

        return this;
    }

    /**
     * check if element has class
     * @param {array} classToCheck - class to check the element has.
     * @returns {boolean} - if element has class (true) or not (false)
     */
    hasClass(classes)
    {
        return this.element.classList.contains(classes)
    }

    /**
     * add ID to Element
     * @param {string} id - ID to add to element
     */
    addID(id)
    {
        this.element.setAttribute('id', id);

        return this;
    }

    /**
     * add multiple attributes to element
     * @param {array} attributions - array of objects containing attribute and value (optional) props as strings
     */
    addMultipleAttributes(attributions)
    {

        if (typeof attributions === 'object' && attributions !== null && attributions.length)
        {
            let i;
            for (i = 0; i < attributions.length; i++)
            {
                this.addAttribute(attributions[i].attribute, attributions[i].value);
            }
        }
        else if (typeof attributions === 'object' && attributions !== null)
        {
            this.addAttribute(attributions.attribute, attributions.value);
        }
        else
        {
            this.throwError('Element.addMultipleAttributes: attributions must be an object or an array of objects');
        }

        return this;
    }

    /**
     * add attribute to element
     * @param {string} attribute - attribute name
     * @param {string} value - attribute value (optional)
     */
    addAttribute(attribute, value)
    {
        if (attribute)
        {
            (attribute === 'class') ? this.addClass(value) : value ?
                this.element.setAttribute(attribute, value) : this.element.setAttribute(attribute, '');
        }
        else
        {
            this.throwError('Element.addAttribute: no attribute passed')
        }

        return this;
    }

    /**
     * remove Element's previous content and then add new content
     * @param content - content to be added
     */
    updateContent(content)
    {
        this.element.innerHTML = '';
        this.append(content);
        return this;
    }

    /**
     * add new HTML to implement. Useful for templating
     * @param HTML - HTML to update
     */
    updateHTML(HTML)
    {
        this.element.innerHTML = HTML;

        return this;
    }

    /**
     * take the contents of the element and wrap in another element
     * @param {object} wrapperElementOptions - element to wrap contents in:
     * takes all the props the Element constructor takes: {classes, content, id, element, attributions}
     */
    wrapContent(wrapperElementOptions)
    {
        const wrapper = new Element(wrapperElementOptions);
        [...this.returnElement().childNodes].forEach((child) => wrapper.append(child));
        this.append(wrapper.returnElement());

        return this;
    }


    /**
     * append something to the element
     * @param {element / node / string} item - thing to append, can be an element or a node
     */
    append(item)
    {
        item = (typeof item !== 'object') ? document.createTextNode(item) : item;
        this.element.appendChild(item);
        return this;
    }

    /**
     * prepend something to the element
     * @param - thing to prepend, can be an element or a node
     */
    prepend(item)
    {
        this.element.prepend(item);

        return this;
    }

    /**
     * return the contents of the element
     * @returns {HTMLCollection} - contents of the element
     */
    retrieveContents()
    {
        return this.element.children;
    }

    /**
     * return element as a node (remove from object).
     * @returns {node} - element as a node
     */
    returnElement()
    {
        return this.element;
    }

    throwError(message)
    {
        throw new Error(message);
    }
}
